import ProjRef from "@/shared/components/ProjRef";
import { useEffect, useState, useCallback } from "react";
import api, { fmtErr } from "@/shared/lib/api";
import {
  Layers, ArrowLeft, ChevronRight, FolderOpen, CheckCircle2,
  Lock, UserCheck, Sparkles,
} from "lucide-react";
import { toast } from "sonner";

/* PSM Pre-Press board (Msg 939) — assigned active projects; for every wall whose
   design is APPROVED the PSM picks the pre-press person (per wall or per
   sub-scope group). Walls without an approved design are locked until approval. */

export default function PrePressBoard() {
  const [projects, setProjects] = useState(null);
  const [code, setCode] = useState(null);

  const load = useCallback(() => {
    api.get("/prepress-board")
      .then((r) => setProjects(r.data.projects || []))
      .catch((e) => { toast.error(fmtErr(e.response?.data?.detail) || "Failed to load"); setProjects([]); });
  }, []);
  useEffect(() => { load(); }, [load]);

  const active = code ? (projects || []).find((p) => p.project_code === code) : null;

  return (
    <div className="p-5 sm:p-8 max-w-6xl mx-auto" data-testid="prepress-board-page">
      {projects === null ? (
        <div className="py-24 text-center text-zinc-400">Loading…</div>
      ) : active ? (
        <ProjectView project={active} onBack={() => setCode(null)} onChanged={load} />
      ) : (
        <ProjectList projects={projects} onOpen={setCode} />
      )}
    </div>
  );
}

function ProjectList({ projects, onOpen }) {
  return (
    <div data-testid="prepress-project-list">
      <div className="flex items-center gap-3 mb-1">
        <div className="w-10 h-10 rounded-xl bg-violet-600 text-white flex items-center justify-center">
          <Layers size={20} strokeWidth={1.8} />
        </div>
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-zinc-900">Pre-Press Assignment</h1>
          <p className="text-sm text-zinc-500">Assign a pre-press person to each wall once its design is approved.</p>
        </div>
      </div>
      {projects.length === 0 ? (
        <div className="mt-10 py-20 text-center text-zinc-400 border border-dashed border-zinc-200 rounded-2xl">
          No active projects assigned to you yet.
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-6">
          {projects.map((p) => {
            const open = p.ready_count - p.assigned_count;
            return (
              <button key={p.project_code} onClick={() => onOpen(p.project_code)}
                data-testid={`prepress-project-${p.project_code}`}
                className="text-left rounded-2xl border border-zinc-200 shadow-sm bg-white p-5 hover:border-violet-300 hover:shadow-md transition-all">
                <div className="flex items-center justify-between">
                  <span className="font-mono text-[11px] tracking-widest text-zinc-400 uppercase"><ProjRef code={p.project_code} /></span>
                  <ChevronRight size={16} className="text-zinc-300" />
                </div>
                <div className="mt-1 font-semibold text-zinc-900 line-clamp-2">{p.project_name}</div>
                <div className="mt-4 flex items-center gap-4 text-sm">
                  <span className="text-zinc-500">{p.total} walls</span>
                  {open > 0 ? (
                    <span className="inline-flex items-center gap-1 text-violet-700 font-medium">
                      <Sparkles size={14} /> {open} ready to assign
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-emerald-600 font-medium">
                      <CheckCircle2 size={14} /> all set
                    </span>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

function ProjectView({ project, onBack, onChanged }) {
  // group walls by sub-scope
  const groups = {};
  const order = [];
  (project.lines || []).forEach((w) => {
    if (!groups[w.group]) { groups[w.group] = []; order.push(w.group); }
    groups[w.group].push(w);
  });
  const options = project.prepress_options || [];
  const [checked, setChecked] = useState(new Set());
  const [bulkUser, setBulkUser] = useState("");
  const [bulkReason, setBulkReason] = useState("");
  const [bulkSaving, setBulkSaving] = useState(false);

  const lineById = {};
  (project.lines || []).forEach((w) => { lineById[w.boq_id] = w; });
  const needsReason = bulkUser && Array.from(checked).some((id) => {
    const l = lineById[id];
    return l && (l.prepress_id || "") && l.prepress_id !== bulkUser;
  });

  function toggle(id) {
    setChecked((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  }

  function toggleGroup(walls) {
    const ready = walls.filter((w) => w.design_finalized).map((w) => w.boq_id);
    setChecked((s) => {
      const n = new Set(s);
      const allIn = ready.every((id) => n.has(id));
      ready.forEach((id) => (allIn ? n.delete(id) : n.add(id)));
      return n;
    });
  }

  async function bulkAssign() {
    if (!bulkUser) { toast.error("Pick a pre-press person."); return; }
    if (checked.size === 0) { toast.error("Select at least one wall."); return; }
    if (needsReason && !bulkReason.trim()) {
      toast.error("Some selected walls already have a different pre-press person. Give a reason.");
      return;
    }
    setBulkSaving(true);
    try {
      const { data } = await api.post(`/projects/${project.project_code}/walls/prepress-bulk-assign`, {
        prepress_id: bulkUser, wall_ids: Array.from(checked), reason: bulkReason.trim(),
      });
      toast.success(`Assigned ${data.updated} wall(s)${data.skipped_unapproved ? ` · ${data.skipped_unapproved} skipped (not approved)` : ""}`);
      setChecked(new Set()); setBulkUser(""); setBulkReason("");
      onChanged();
    } catch (e) {
      toast.error(fmtErr(e.response?.data?.detail) || "Bulk assign failed");
    } finally { setBulkSaving(false); }
  }

  async function assign(wid, prepress_id, currentId) {
    let reason = "";
    if (currentId && currentId !== prepress_id) {
      reason = (window.prompt(
        prepress_id
          ? "Pre-press is already assigned for this wall. Give a reason to reassign:"
          : "Pre-press is already assigned for this wall. Give a reason to clear it:"
      ) || "").trim();
      if (!reason) { toast.error("A reason is required to change pre-press."); return; }
    }
    try {
      await api.patch(`/projects/${project.project_code}/boq/${wid}/prepress`, { prepress_id, reason });
      toast.success(prepress_id ? (reason ? "Pre-press reassigned" : "Pre-press assigned") : "Pre-press cleared");
      onChanged();
    } catch (e) {
      toast.error(fmtErr(e.response?.data?.detail) || "Failed");
    }
  }

  async function assignGroup(walls, prepress_id) {
    const ready = walls.filter((w) => w.design_finalized);
    if (!ready.length) { toast.error("No approved walls in this sub-scope yet."); return; }
    const needsReason = ready.some((w) => (w.prepress_id || "") && w.prepress_id !== prepress_id);
    let reason = "";
    if (needsReason) {
      reason = (window.prompt("Some of these walls already have a pre-press person. Give a reason to reassign:") || "").trim();
      if (!reason) { toast.error("A reason is required to change pre-press."); return; }
    }
    try {
      await Promise.all(ready.map((w) =>
        api.patch(`/projects/${project.project_code}/boq/${w.boq_id}/prepress`, { prepress_id, reason })));
      toast.success(`Assigned ${ready.length} wall(s)`);
      onChanged();
    } catch (e) {
      toast.error(fmtErr(e.response?.data?.detail) || "Failed");
    }
  }

  return (
    <div data-testid="prepress-project-view">
      <button onClick={onBack} data-testid="prepress-back-btn"
        className="inline-flex items-center gap-1.5 text-sm text-zinc-500 hover:text-zinc-800 mb-4">
        <ArrowLeft size={16} /> All projects
      </button>
      <div className="flex items-center justify-between flex-wrap gap-2 mb-6">
        <div>
          <span className="font-mono text-[11px] tracking-widest text-zinc-400 uppercase"><ProjRef code={project.project_code} /></span>
          <h1 className="text-2xl font-bold tracking-tight text-zinc-900">{project.project_name}</h1>
        </div>
        <div className="text-sm text-zinc-500">
          {project.assigned_count}/{project.ready_count} approved walls assigned
        </div>
      </div>

      {options.length === 0 && (
        <div className="mb-6 p-4 rounded-xl bg-amber-50 border border-amber-200 text-amber-800 text-sm">
          No pre-press people on this project yet. Add them first via the project's <b>Pre-Press Team</b> section, then come back here.
        </div>
      )}

      <div className="space-y-6">
        {order.map((g) => {
          const walls = groups[g];
          const readyCount = walls.filter((w) => w.design_finalized).length;
          return (
            <div key={g} className="rounded-2xl border border-zinc-200 shadow-sm bg-white overflow-hidden"
              data-testid={`prepress-group-${g}`}>
              <div className="flex items-center justify-between gap-3 px-5 py-3 bg-zinc-50 border-b border-zinc-200">
                <div className="flex items-center gap-2">
                  {options.length > 0 && readyCount > 0 && (
                    <input type="checkbox" data-testid={`prepress-group-check-${g}`}
                      checked={walls.filter((w) => w.design_finalized).every((w) => checked.has(w.boq_id))}
                      onChange={() => toggleGroup(walls)}
                      className="w-4 h-4 accent-violet-600 cursor-pointer" title="Select all approved walls" />
                  )}
                  <FolderOpen size={18} className="text-violet-600" strokeWidth={1.6} />
                  <span className="font-semibold text-zinc-900">{g}</span>
                  <span className="text-xs text-zinc-400">· {walls.length} wall(s) · {readyCount} approved</span>
                </div>
                {options.length > 0 && readyCount > 0 && (
                  <select defaultValue="" data-testid={`prepress-group-assign-${g}`}
                    onChange={(e) => { if (e.target.value) { assignGroup(walls, e.target.value); e.target.value = ""; } }}
                    className="text-xs border border-zinc-200 rounded-lg px-2 py-1.5 bg-white text-zinc-700 hover:border-violet-300">
                    <option value="">Assign all approved →</option>
                    {options.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
                  </select>
                )}
              </div>
              <div className="divide-y divide-zinc-100">
                {walls.map((w) => (
                  <div key={w.boq_id} className="flex items-center justify-between gap-3 px-5 py-3"
                    data-testid={`prepress-wall-${w.boq_id}`}>
                    <div className="flex items-center gap-3 min-w-0">
                      {w.design_finalized && options.length > 0 && (
                        <input type="checkbox" checked={checked.has(w.boq_id)}
                          onChange={() => toggle(w.boq_id)} data-testid={`prepress-bulk-check-${w.boq_id}`}
                          className="w-4 h-4 accent-violet-600 cursor-pointer shrink-0" />
                      )}
                      <div className="min-w-0">
                        <div className="font-medium text-zinc-900 truncate">
                          {w.wall_label}
                          {w.linked_count > 1 && (
                            <span className="ml-1.5 inline-flex items-center px-1.5 py-0.5 rounded-full bg-indigo-100 text-indigo-700 text-[10px] font-semibold whitespace-nowrap"
                                  data-testid={`prepress-linked-badge-${w.boq_id}`}>1 file × {w.linked_count}</span>
                          )}
                        </div>
                        <div className="text-xs text-zinc-400 truncate">
                          {w.title}{w.designer_name ? ` · designer ${w.designer_name}` : ""}
                        </div>
                      </div>
                    </div>
                    {w.design_finalized ? (
                      <div className="flex items-center gap-2 shrink-0">
                        {w.prepress_name && (
                          <span className="inline-flex items-center gap-1 text-xs text-emerald-700">
                            <UserCheck size={13} /> {w.prepress_name}
                          </span>
                        )}
                        <select value={w.prepress_id || ""} data-testid={`prepress-select-${w.boq_id}`}
                          onChange={(e) => assign(w.boq_id, e.target.value, w.prepress_id || "")}
                          className="text-sm border border-zinc-200 rounded-lg px-2 py-1.5 bg-white text-zinc-700 hover:border-violet-300 focus:border-violet-500 outline-none">
                          <option value="">— unassigned —</option>
                          {options.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
                        </select>
                      </div>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-xs text-zinc-400 shrink-0"
                        data-testid={`prepress-locked-${w.boq_id}`}>
                        <Lock size={13} /> awaiting design approval
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {checked.size > 0 && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-40 bg-zinc-900 text-white rounded-2xl shadow-2xl px-5 py-3 flex items-center gap-3 flex-wrap max-w-[92vw]"
             data-testid="prepress-bulk-bar">
          <span className="text-sm font-medium whitespace-nowrap">{checked.size} wall(s) selected</span>
          <select value={bulkUser} onChange={(e) => setBulkUser(e.target.value)}
            data-testid="prepress-bulk-user"
            className="text-sm rounded-lg px-2 py-1.5 bg-zinc-800 border border-zinc-700 text-white outline-none">
            <option value="">Assign to…</option>
            {options.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
          </select>
          {needsReason && (
            <input value={bulkReason} onChange={(e) => setBulkReason(e.target.value)}
              placeholder="Reason for reassignment…" data-testid="prepress-bulk-reason"
              className="text-sm rounded-lg px-3 py-1.5 bg-zinc-800 border border-amber-500/60 text-white placeholder:text-zinc-400 outline-none w-56" />
          )}
          <button onClick={bulkAssign} disabled={bulkSaving || !bulkUser}
            data-testid="prepress-bulk-assign-btn"
            className="text-sm font-medium bg-violet-500 hover:bg-violet-400 disabled:opacity-50 rounded-lg px-4 py-1.5">
            {bulkSaving ? "Assigning…" : "Assign"}
          </button>
          <button onClick={() => { setChecked(new Set()); setBulkUser(""); setBulkReason(""); }}
            data-testid="prepress-bulk-clear" className="text-sm text-zinc-300 hover:text-white">
            Clear
          </button>
        </div>
      )}
    </div>
  );
}
